/**
 * `microphoneHealth.ts` — detects a microphone capture that stopped delivering
 * audio mid-session, and paces the reacquire attempts. Used by the realtime
 * transport; Unity's equivalent lives in `AIRealtimeTransportModelRTC.cs`
 * (`RestartMicrophoneIfStalled` / `TryRestartMicrophone`).
 *
 * Starting a screen recorder (OBS, a system recorder) can end, mute, or
 * silently starve a live `getUserMedia` track; before this, only a page
 * refresh got the mic back.
 *
 * Trace events (same names Unity writes):
 * - `microphone.restarted` — `reason=, attempt=, stalled_ms=, started=, transport=`
 * - `microphone.recovered` — `attempts=, lost_ms=, transport=`
 *
 * Not yet driven in-browser (AI-1527). To verify: open a project, hold
 * push-to-talk and talk to Spark, start an OBS recording that captures the
 * mic, then talk again. Spark should hear you without a refresh, and the
 * trace should show `microphone.restarted` (`started=true`) then
 * `microphone.recovered`, with `ptt.uplink` packets advancing afterwards.
 * Unplugging the mic mid-session exercises the `track_ended` path. Chrome
 * reports `media-source` stats; a browser without them gets only the track
 * events.
 */

export type MicrophoneRestartReason =
  | "track_ended"
  | "track_muted"
  | "device_changed"
  | "source_stalled"
  | "source_silent";

export interface MicrophoneDiagnostic {
  eventType: string;
  summary: string;
  payload: Record<string, unknown>;
}

/** Poll cadence for the health check while a recording window is open. */
export const K_MIC_HEALTH_POLL_MS = 250;
/** A browser `mute` can clear on its own; reacquire only if it outlasts this. */
export const K_MIC_TRACK_MUTE_GRACE_MS = 1000;
/** No captured samples for this long means the source stopped. */
const K_MIC_STALL_MS = 1000;
/** Getting the first samples after acquire can take longer than a steady-state gap. */
const K_MIC_STARTUP_GRACE_MS = 2000;
/** Samples keep arriving but are exact digital zeros: a starved, not quiet, mic. */
const K_MIC_SILENT_MS = 2500;
const K_MIC_RESTART_INITIAL_BACKOFF_MS = 1000;
const K_MIC_RESTART_MAX_BACKOFF_MS = 8000;

/** Decides when a capture stopped, from cumulative sample and energy counters. */
export class MicrophoneHealthMonitor {
  private m_lastSamples = -1;
  private m_lastEnergy = -1;
  private m_samplesAdvancedAt = 0;
  private m_energyAdvancedAt = 0;
  private m_hasAdvanced = false;
  private m_hasAudio = false;
  private m_lastAudioAt = 0;

  /** Call when a recording window opens or a new source is attached. */
  reset(nowMs: number): void {
    this.m_lastSamples = -1;
    this.m_lastEnergy = -1;
    this.m_samplesAdvancedAt = nowMs;
    this.m_energyAdvancedAt = nowMs;
    this.m_hasAdvanced = false;
    this.m_hasAudio = false;
  }

  /** Last time real (non-zero) audio was observed; 0 if never. */
  get lastAudioAt(): number {
    return this.m_lastAudioAt;
  }

  /** Samples and non-zero energy have both advanced since the last reset. */
  get isDeliveringAudio(): boolean {
    return this.m_hasAdvanced && this.m_hasAudio;
  }

  /**
   * @param capturedSeconds cumulative seconds of audio the source produced
   * @param totalEnergy cumulative energy of that audio
   * @returns why the capture looks dead, or null while audio still arrives
   */
  observe(
    capturedSeconds: number,
    totalEnergy: number,
    nowMs: number,
  ): "source_stalled" | "source_silent" | null {
    if (capturedSeconds !== this.m_lastSamples) {
      if (this.m_lastSamples >= 0) this.m_hasAdvanced = true;
      this.m_lastSamples = capturedSeconds;
      this.m_samplesAdvancedAt = nowMs;
    }
    if (totalEnergy !== this.m_lastEnergy) {
      if (this.m_lastEnergy >= 0) {
        this.m_hasAudio = true;
        this.m_lastAudioAt = nowMs;
      }
      this.m_lastEnergy = totalEnergy;
      this.m_energyAdvancedAt = nowMs;
    }

    const stallLimit = this.m_hasAdvanced ? K_MIC_STALL_MS : K_MIC_STARTUP_GRACE_MS;
    if (nowMs - this.m_samplesAdvancedAt > stallLimit) return "source_stalled";
    if (this.m_hasAdvanced && nowMs - this.m_energyAdvancedAt > K_MIC_SILENT_MS) {
      return "source_silent";
    }
    return null;
  }
}

/** Exponential backoff and attempt accounting for mic reacquires. */
export class MicrophoneRestartBackoff {
  private m_attempts = 0;
  private m_backoffMs = K_MIC_RESTART_INITIAL_BACKOFF_MS;
  private m_nextAllowedAt = 0;
  private m_lostSince = 0;

  get attempts(): number {
    return this.m_attempts;
  }

  /** Reserve an attempt; false while backing off. */
  tryBegin(nowMs: number, lostSinceMs: number): boolean {
    if (nowMs < this.m_nextAllowedAt) return false;
    if (this.m_attempts === 0) this.m_lostSince = lostSinceMs || nowMs;
    this.m_attempts += 1;
    this.m_nextAllowedAt = nowMs + this.m_backoffMs;
    this.m_backoffMs = Math.min(this.m_backoffMs * 2, K_MIC_RESTART_MAX_BACKOFF_MS);
    return true;
  }

  /** Audio is flowing again; returns the recovery note fields if a restart preceded it. */
  recovered(nowMs: number): { attempts: number; lostMs: number } | null {
    if (this.m_attempts === 0) return null;
    const result = { attempts: this.m_attempts, lostMs: Math.round(nowMs - this.m_lostSince) };
    this.reset();
    return result;
  }

  reset(): void {
    this.m_attempts = 0;
    this.m_backoffMs = K_MIC_RESTART_INITIAL_BACKOFF_MS;
    this.m_nextAllowedAt = 0;
    this.m_lostSince = 0;
  }
}

export function microphoneRestartedNote(
  reason: MicrophoneRestartReason,
  attempt: number,
  stalledMs: number,
  started: boolean,
  transport: string,
): MicrophoneDiagnostic {
  const stalled = Math.max(0, Math.round(stalledMs));
  return {
    eventType: "microphone.restarted",
    summary: `reason=${reason}, attempt=${attempt}, stalled_ms=${stalled}, started=${started}, transport=${transport}`,
    payload: { reason, attempt, stalled_ms: stalled, started, transport },
  };
}

export function microphoneRecoveredNote(
  attempts: number,
  lostMs: number,
  transport: string,
): MicrophoneDiagnostic {
  return {
    eventType: "microphone.recovered",
    summary: `attempts=${attempts}, lost_ms=${lostMs}, transport=${transport}`,
    payload: { attempts, lost_ms: lostMs, transport },
  };
}
